import { useEffect, useState } from 'react';

import {
  api,
  type EtteremLista,
  type EtteremSor,
  type TelephelyAdatok,
  type TelephelyParositasEredmeny,
} from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Telephely-párosító panel. (2.0 kiegészítés — parositas-kiegeszites.md)
 *
 * Egy párosítatlan számlához keres éttermet: fent a pontozott javaslatok,
 * alatta kereső, legalul az új étteremként felvétel.
 *
 * Amit kiválasztasz, az **az összetartozó összes számlára** érvényes:
 * telephelynév esetén az azonos nevűekre, telephelynév nélkül az azonos
 * cégnevűekre. Egy kattintás így tucatnyi számlát rendez.
 */
export function TelephelyParosito({
  telephelyNev,
  szamlaId,
  onKesz,
  onBezar,
}: {
  telephelyNev: string;
  /** A számla, amelyikről indult — a telephelynév nélküliekhez kell. */
  szamlaId: number | null;
  onKesz: (uzenet: string) => void;
  onBezar: () => void;
}) {
  const [adatok, setAdatok] = useState<TelephelyAdatok | null>(null);
  const [kereses, setKereses] = useState('');
  const [talalatok, setTalalatok] = useState<EtteremSor[]>([]);
  const [munkaban, setMunkaban] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);

  const nevNelkul = telephelyNev.trim() === '';

  useEffect(() => {
    const p = new URLSearchParams({ nev: telephelyNev });
    if (szamlaId !== null) p.set('szamla_id', String(szamlaId));
    api
      .get<TelephelyAdatok>(`/api/szamla/telephely-parositas/javaslat?${p}`)
      .then(setAdatok)
      .catch((h: Error) => setHiba(h.message));
  }, [telephelyNev, szamlaId]);

  /* A kereső kettőnél több betűtől keres, legfeljebb nyolc találattal. */
  useEffect(() => {
    const q = kereses.trim();
    if (q.length <= 2) {
      setTalalatok([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const e = await api.get<EtteremLista>(`/api/etterem?q=${encodeURIComponent(q)}&limit=8`);
        setTalalatok(e.sorok.slice(0, 8));
      } catch {
        setTalalatok([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [kereses]);

  function kesz(e: TelephelyParositasEredmeny) {
    const uj = e.uj_etterem ? ' (új étteremként felvéve)' : '';
    if (e.csoport === 'telephely') {
      onKesz(
        `${ft(e.szamla_db)} számla a helyére került: ${e.etterem_nev}${uj}. ` +
          'A program megjegyezte — a következő importnál magától felismeri.',
      );
    } else if (e.csoport === 'cegnev') {
      onKesz(
        `${ft(e.szamla_db)} telephelynév nélküli, azonos cégnevű számla a helyére került: ` +
          `${e.etterem_nev}${uj}.`,
      );
    } else {
      onKesz(`A számla a helyére került: ${e.etterem_nev}${uj}.`);
    }
  }

  async function parosit(etterem_id: number) {
    setMunkaban(true);
    setHiba(null);
    try {
      kesz(
        await api.post<TelephelyParositasEredmeny>('/api/szamla/telephely-parositas', {
          telephely_nev: telephelyNev,
          etterem_id,
          szamla_id: szamlaId,
        }),
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A párosítás nem sikerült.');
      setMunkaban(false);
    }
  }

  async function ujkent() {
    setMunkaban(true);
    setHiba(null);
    try {
      kesz(
        await api.post<TelephelyParositasEredmeny>('/api/szamla/telephely-parositas/uj-etterem', {
          telephely_nev: telephelyNev,
          szamla_id: szamlaId,
        }),
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az étterem nem vehető fel.');
      setMunkaban(false);
    }
  }

  const ujNev = adatok?.uj_etterem_nev ?? null;

  return (
    <div className="tdet tpar">
      <h4>Telephely párosítása — {nevNelkul ? '(nincs telephelynév)' : telephelyNev}</h4>
      <p className="hint" style={{ margin: '0 0 10px' }}>
        {!adatok ? (
          'Betöltés…'
        ) : adatok.csoport === 'telephely' ? (
          <>
            <b>{ft(adatok.szamla_db)}</b> számla tartozik ehhez a névhez
            {adatok.cegnev && (
              <>
                {' '}
                · a számlán szereplő cég: <b>{adatok.cegnev}</b>
              </>
            )}
          </>
        ) : adatok.csoport === 'cegnev' ? (
          <>
            Telephelynév nélküli számla. A(z) <b>{adatok.cegnev}</b> cégnévvel{' '}
            <b>{ft(adatok.szamla_db)}</b> ilyen számla van — a párosítás mindet egyszerre
            rendezi.
          </>
        ) : (
          'Ezen a számlán se telephelynév, se cégnév nincs — csak ez az egy számla párosul.'
        )}
      </p>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}

      <p className="lbl">Javasolt éttermek</p>
      {adatok && adatok.javaslatok.length === 0 && (
        <p className="hint">Nincs hasonló nevű étterem — keress rá lent, vagy vedd fel újként.</p>
      )}
      <div className="tpar-kartyak">
        {adatok?.javaslatok.map((j) => (
          <Kartya
            key={j.etterem_id}
            nev={j.nev}
            cim={j.cim}
            cegnev={j.cegnev}
            indok={j.indok}
            tiltva={munkaban}
            onValaszt={() => void parosit(j.etterem_id)}
          />
        ))}
      </div>

      <p className="lbl">Ha egyik sem az: keresés</p>
      <input
        value={kereses}
        placeholder="étterem neve vagy címe — legalább 3 betű"
        onChange={(e) => setKereses(e.target.value)}
        style={{ textAlign: 'left', fontFamily: 'inherit' }}
      />
      {talalatok.length > 0 && (
        <div className="tpar-kartyak" style={{ marginTop: 6 }}>
          {talalatok.map((t) => (
            <Kartya
              key={t.id}
              nev={t.nev}
              cim={[t.irsz, t.cim].filter(Boolean).join(' ') || null}
              cegnev={t.cegnev}
              tiltva={munkaban}
              onValaszt={() => void parosit(t.id)}
            />
          ))}
        </div>
      )}

      <div className="btns">
        <button
          className="btn sm"
          disabled={munkaban || !ujNev}
          title={
            ujNev
              ? `Új étterem „${ujNev}” néven, a számlán szereplő cégnévvel`
              : 'Nincs a számlán név, amivel az új éttermet fel lehetne venni'
          }
          onClick={() => void ujkent()}
        >
          Felvétel új étteremként
        </button>
        <button className="btn sm" onClick={onBezar}>
          Mégsem
        </button>
        {adatok?.csoport === 'cegnev' && ujNev && (
          <span className="hint">
            Az új étterem a cégnevet kapja névként („{ujNev}") — a törzsben később átírhatod.
          </span>
        )}
      </div>
    </div>
  );
}

function Kartya({
  nev,
  cim,
  cegnev,
  indok,
  tiltva,
  onValaszt,
}: {
  nev: string;
  cim: string | null;
  cegnev: string | null;
  indok?: string;
  tiltva: boolean;
  onValaszt: () => void;
}) {
  return (
    <button className="tpar-kartya" disabled={tiltva} onClick={onValaszt}>
      <b>{nev}</b>
      <small>{cim ?? 'nincs cím'}</small>
      {cegnev && <small>{cegnev}</small>}
      {indok && <i>{indok}</i>}
    </button>
  );
}
